import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  Mail,
  Search,
  RefreshCw,
  Plus,
  Send,
  CheckSquare,
  Handshake,
  Folder,
  ExternalLink,
  ChevronDown,
  X,
  Info,
  FileText,
  IdCard,
  Check,
  ArrowLeft,
  HelpCircle,
  Sparkles,
} from "lucide-react";
import {
  INK,
  INK_2,
  INK_3,
  LINE,
  LINE_2,
  BURGUNDY,
  BURGUNDY_SOFT,
  BURGUNDY_TINT,
  BAD_INK,
  INFO_BG,
  INFO_INK,
  NEUT_BG,
} from "../Bureau/bureauTokens";
import { httpClient, authFetch } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import mailsTutorial from "../Tutoriel/MailsTutorial";
import useGmailAccount from "./useGmailAccount";
import Evenement from "../Evenement/Evenement.js";
import Mission from "../Mission/Mission.js";

// Modèles de réponse — texte statique de la maquette, aucune donnée à
// inventer ici (ce sont juste des trames toutes faites).
const MAIL_TEMPLATES = [
  { id: "blank", name: "— Vierge —", subject: "", body: "" },
  {
    id: "relance_pieces",
    name: "Relance — pièces manquantes",
    subject: "Relance — pièces comptables manquantes",
    body: "Bonjour,\n\nSauf erreur de notre part, nous n'avons pas encore reçu les pièces suivantes nécessaires à l'établissement de votre comptabilité :\n\n- \n- \n\nPourriez-vous nous les transmettre dès que possible afin de respecter les échéances ?\n\nBien cordialement,\nCabinet ALFA Partenaires",
  },
  {
    id: "accuse_pieces",
    name: "Accusé de réception de pièces",
    subject: "Bonne réception de vos pièces",
    body: "Bonjour,\n\nNous accusons bonne réception de vos pièces comptables. Nous procédons à leur traitement et revenons vers vous en cas de besoin.\n\nBien cordialement,\nCabinet ALFA Partenaires",
  },
  {
    id: "rdv",
    name: "Proposition de rendez-vous",
    subject: "Proposition de rendez-vous",
    body: "Bonjour,\n\nAfin de faire le point sur votre dossier, je vous propose un rendez-vous le [DATE] à [HEURE], au cabinet ou en visioconférence.\n\nCe créneau vous convient-il ?\n\nBien à vous,",
  },
  {
    id: "echeance",
    name: "Rappel d'échéance déclarative",
    subject: "Rappel — échéance déclarative à venir",
    body: "Bonjour,\n\nNous vous rappelons que l'échéance suivante approche : [DÉCLARATION] à déposer avant le [DATE].\n\nMerci de nous transmettre les éléments nécessaires dans les meilleurs délais.\n\nBien cordialement,\nCabinet ALFA Partenaires",
  },
  {
    id: "onboarding",
    name: "Demande de documents (onboarding)",
    subject: "Bienvenue — documents à nous transmettre",
    body: "Bonjour,\n\nBienvenue au sein du cabinet. Afin d'ouvrir votre dossier, merci de nous transmettre :\n\n- Pièce d'identité du dirigeant\n- Extrait Kbis / statuts\n- Dernière liasse fiscale\n- RIB\n\nRestant à votre disposition,\nCabinet ALFA Partenaires",
  },
  {
    id: "honoraires",
    name: "Relance règlement honoraires",
    subject: "Relance — règlement de nos honoraires",
    body: "Bonjour,\n\nSauf omission de notre part, la facture n°[N°] d'un montant de [MONTANT] € reste à ce jour impayée.\n\nNous vous remercions de bien vouloir procéder à son règlement.\n\nBien cordialement,\nService comptable — Cabinet ALFA Partenaires",
  },
];

function formatMsgDate(date) {
  if (!date) return "";
  const d = new Date(date);
  return isNaN(d) ? "" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function initialsOf(name) {
  return (name || "?").trim().slice(0, 2).toUpperCase();
}

function detectPhone(text) {
  const mt = (text || "").match(/0[1-9](?:[ .]?\d{2}){4}/);
  return mt ? mt[0] : null;
}

// L'API ne permet pas de télécharger les pièces jointes ni de marquer
// lu/non lu avec les scopes readonly+send — on renvoie donc vers Gmail pour
// tout ce qui dépasse la lecture/réponse.
function gmailMessageUrl(id) {
  return `https://mail.google.com/mail/u/0/#all/${id}`;
}

// Affiche un mail HTML (boutons, images, mise en forme...) de façon sûre :
// nettoyé par DOMPurify PUIS rendu dans une iframe sandboxée sans
// "allow-scripts" ni "allow-same-origin" — aucun script du mail ne peut
// s'exécuter ni accéder au reste de l'app, même si un contenu malveillant
// passait au travers du nettoyage. "allow-popups"/"allow-popups-to-escape-sandbox"
// laissent quand même les liens du mail s'ouvrir dans un nouvel onglet.
function HtmlEmailBody({ html }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(200);

  const sanitized = useMemo(
    () => DOMPurify.sanitize(html, { WHOLE_DOCUMENT: false, ADD_ATTR: ["target"] }),
    [html],
  );

  const resize = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.body) setHeight(doc.body.scrollHeight + 20);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Contenu du mail"
      // srcDoc = document HTML à part : "inherit" n'y récupère pas la police
      // de la page parente. Le but ici n'est pas la cohérence avec l'app
      // mais la fidélité à Gmail (mail affiché "comme dans Gmail") — pour
      // un mail sans style propre (ex. mail interne tapé sans mise en
      // forme), on reproduit donc la police/taille par défaut de Gmail
      // (Arial/Helvetica, 13px) plutôt que la police de marque de l'app.
      // Un mail qui définit son propre style (cas de la plupart des mails
      // HTML "pro"/marketing) garde le sien, ceci n'étant qu'un défaut.
      srcDoc={`<!DOCTYPE html><html><head><base target="_blank"><meta name="color-scheme" content="light"></head><body style="margin:0;font-family:arial,helvetica,sans-serif;font-size:13px;line-height:1.6;">${sanitized}</body></html>`}
      onLoad={resize}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className="flex-1"
      // minHeight (pas height) : un mail court remplit quand même tout
      // l'espace dispo (flex-1 dans son parent flex), un mail long grandit
      // au-delà sans être rogné — le conteneur défilant au-dessus prend le
      // relais du défilement dans ce cas.
      style={{ width: "100%", minHeight: height, border: "none", display: "block" }}
    />
  );
}

// Classification par mots-clés (pas une IA — un simple filtre déterministe
// sur l'objet/aperçu/expéditeur) : faute d'un vrai backend de classification
// pour de vrais mails Gmail, c'est la seule façon honnête de faire
// apparaître les badges catégorie/priorité de la maquette sans inventer de
// données. À remplacer si un jour un vrai classifieur existe.
function classifyMail(m) {
  const from = (m.from.email || "").toLowerCase();
  const text = `${m.subject} ${m.snippet}`.toLowerCase();
  if (from.endsWith("@alfa-partenaires.re") || from.endsWith("@alfa-partenaires.com")) return "Interne";
  if (/rendez-vous|\brdv\b|disponib|cr[ée]neau/.test(text)) return "Demande de rdv";
  if (/urssaf|\bdsn\b|\bpaie\b|bulletin de salaire|cotisation|social/.test(text)) return "Social";
  if (/statuts|kbis|greffe|assembl|immatricul|g[ée]rance|cession de parts|pv d'assembl/.test(text)) return "Juridique";
  if (/pi[èe]ce|justificatif|relev[ée]|facture/.test(text)) return "Arrivée pièces comptables";
  if (/\btva\b|\bis\b|\bcfe\b|imp[ôo]t|fiscal|d[ée]claration|bilan/.test(text)) return "Compta / fiscal";
  return "Autre";
}

// La catégorie d'un mail (classifyMail) et le champ "type" d'Evenement.js
// partagent presque les mêmes valeurs — seule "Interne" n'existe pas côté
// Evenement (pas de catégorie équivalente), on la ramène donc sur "Autre".
function mapMailCategoryToEvenementType(category) {
  return category === "Interne" ? "Autre" : category;
}

const selectClass = "w-full h-9 pl-3 pr-8 text-xs outline-none appearance-none bg-white cursor-pointer";
const selectStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK_2 };
const inputClass = "w-full h-9 px-3 text-xs outline-none";
const inputStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK_2 };
const labelClass = "flex flex-col gap-1.5 text-[10px] font-semibold uppercase tracking-wide";

function Select({ value, onChange, children }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} className={selectClass} style={selectStyle}>
        {children}
      </select>
      <ChevronDown size={14} className="absolute top-1/2 -translate-y-1/2 right-3 pointer-events-none" style={{ color: INK_3 }} />
    </div>
  );
}

// Recherche + sélection d'un dossier client — même pattern que ClientPicker
// (FeuilleTemps.js) : interroge /api/clients/search (léger, id/nom
// uniquement, 25 max) au lieu de charger les ~700 clients d'un coup dans un
// <select>, imbuvable à cette taille.
function DossierPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      httpClient
        .get("/api/clients/search", { params: { search: query } })
        .then(({ data }) => setMatches(Array.isArray(data) ? data.slice(0, 8) : []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="relative">
      <input
        value={value?.nom || q}
        onChange={(e) => {
          onChange(null);
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="— Rattacher à un dossier —"
        className={selectClass}
        style={selectStyle}
      />
      {open && !value && (q.trim() || matches.length > 0) && (
        <div
          className="absolute left-0 right-0 mt-1 overflow-y-auto z-20"
          style={{ top: "100%", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 8, boxShadow: "0 10px 28px rgba(0,0,0,.10)", maxHeight: 180 }}
        >
          {searching && (
            <div className="px-3 py-2 text-xs" style={{ color: INK_3 }}>
              Recherche…
            </div>
          )}
          {!searching && matches.length === 0 && q.trim() && (
            <div className="px-3 py-2 text-xs" style={{ color: INK_3 }}>
              Aucun dossier trouvé
            </div>
          )}
          {matches.map((c) => (
            <button
              key={c.id}
              onMouseDown={() => {
                onChange({ id: c.id, nom: c.nom });
                setQ("");
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 text-left text-xs px-3 py-2"
              style={{ color: INK }}
            >
              {c.nom}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Squelette d'une ligne de la liste des mails — mêmes proportions que la
// vraie ligne (nom + date, objet, aperçu, badges) pour ne pas décaler la
// mise en page une fois les messages arrivés.
function MailRowSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 px-3 py-2.5 animate-pulse" style={{ borderBottom: `1px solid ${LINE}` }}>
      <span className="flex items-center justify-between gap-2">
        <span className="h-2.5 rounded" style={{ width: 120, background: NEUT_BG }} />
        <span className="h-2 rounded flex-shrink-0" style={{ width: 26, background: NEUT_BG }} />
      </span>
      <span className="h-2.5 rounded" style={{ width: "65%", background: NEUT_BG }} />
      <span className="h-2.5 rounded" style={{ width: "85%", background: NEUT_BG }} />
      <span className="h-2.5 rounded mt-0.5" style={{ width: 64, background: NEUT_BG }} />
    </div>
  );
}

// Squelette du volet de lecture pendant le chargement du détail d'un
// message — même structure que le rendu final (en-tête + corps).
function MailDetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex items-start gap-3 px-4 py-3.5" style={{ borderBottom: `1px solid ${LINE}` }}>
        <div className="flex-shrink-0 rounded-full" style={{ width: 36, height: 36, background: NEUT_BG }} />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="h-3 rounded" style={{ width: "45%", background: NEUT_BG }} />
          <div className="h-2.5 rounded" style={{ width: "60%", background: NEUT_BG }} />
        </div>
      </div>
      <div className="flex flex-col gap-2.5 px-4 py-3.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-2.5 rounded" style={{ width: i === 4 ? "40%" : "92%", background: NEUT_BG }} />
        ))}
      </div>
    </div>
  );
}

// Page "Mails" — boîte Gmail connectée, en plein écran (reprend le layout et
// les interactions de MyALFA Worksync Test V2/mails.jsx : liste à gauche,
// lecture détaillée à droite, dossier rattaché, détection de coordonnée,
// mise en tâche/mission, classement). Comme dans la maquette d'origine, le
// rattachement de dossier, la détection de coordonnée et la mise en
// tâche/mission restent des interactions locales (pas de persistance
// serveur) — seuls la lecture, la réponse/transfert et l'envoi sont de
// vrais appels Gmail, la maquette elle-même ne les persistant pas non plus.
function Mails({ onBack }) {
  const {
    connected,
    email,
    messages,
    nextPageToken,
    loadingMessages,
    loadingMore,
    connecting,
    error,
    connect,
    fetchMessages,
    loadMore,
    messageDetails,
    fetchMessageDetail,
    sendMail,
    archiveMessage,
  } = useGmailAccount();

  const [q, setQ] = useState("");
  const [selId, setSelId] = useState(null);
  const [dossiers, setDossiers] = useState({});
  const [dossierClientIds, setDossierClientIds] = useState({});
  const [done, setDone] = useState({});
  const [panel, setPanel] = useState(null); // null | reply | forward
  const [editDoss, setEditDoss] = useState(false);
  const [composeNew, setComposeNew] = useState(false);
  const [tpl, setTpl] = useState("blank");
  const [compose, setCompose] = useState({ to: "", subject: "", text: "" });
  const [aiDrafting, setAiDrafting] = useState(false);
  // "En tâche" / "En mission ponctuelle" : ouvrent les vrais overlays
  // Evenement/Mission (préremplis depuis le mail), plus les mini-panneaux
  // locaux qui ne persistaient rien (voir openTodo/openMission plus bas).
  const [showEvenementOverlay, setShowEvenementOverlay] = useState(false);
  const [evenementPrefill, setEvenementPrefill] = useState(null);
  const [showMissionOverlay, setShowMissionOverlay] = useState(false);
  const [missionPrefill, setMissionPrefill] = useState(null);
  const [readIds, setReadIds] = useState(() => new Set());
  const [archivedIds, setArchivedIds] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (connected) fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // La catégorie (classifyMail) n'est plus affichée (chips/badges retirés,
  // voir plus bas — classification par mots-clés pas assez fiable pour être
  // montrée telle quelle) mais reste calculée : elle alimente encore le
  // prefill "type" d'Evenement.js (voir openTodo).
  const classified = useMemo(
    () => messages.map((m) => ({ ...m, category: classifyMail(m) })),
    [messages]
  );

  const list = useMemo(
    () =>
      classified.filter(
        (m) =>
          !archivedIds.has(m.id) &&
          (!q || (m.from.name + m.subject + m.snippet).toLowerCase().includes(q.toLowerCase()))
      ),
    [classified, archivedIds, q]
  );

  // Une fois la boîte chargée, une conversation reste toujours affichée à
  // droite (même esprit que la maquette, qui présélectionne le 1er mail).
  useEffect(() => {
    if (!selId && list.length > 0) setSelId(list[0].id);
  }, [selId, list]);

  useEffect(() => {
    if (selId) fetchMessageDetail(selId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  const unreadTotal = messages.filter((m) => !archivedIds.has(m.id) && m.unread && !readIds.has(m.id)).length;

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const openMail = (id) => {
    setSelId(id);
    setPanel(null);
    setEditDoss(false);
    setComposeNew(false);
    setReadIds((prev) => new Set(prev).add(id));
  };

  const detail = selId ? messageDetails[selId] : null;
  const dossier = selId ? dossiers[selId] || "" : "";
  const st = (selId && done[selId]) || {};
  const setSt = (patch) => setDone((d) => ({ ...d, [selId]: { ...d[selId], ...patch } }));

  const clearDoss = () => {
    setDossiers((d) => ({ ...d, [selId]: "" }));
    setDossierClientIds((d) => ({ ...d, [selId]: null }));
    setEditDoss(true);
  };
  // v : { id, nom } (voir DossierPicker) ou null (recherche en cours, pas
  // encore de sélection — ne touche pas encore le dossier rattaché).
  const pickDoss = (v) => {
    if (!v) return;
    setDossiers((d) => ({ ...d, [selId]: v.nom }));
    setDossierClientIds((d) => ({ ...d, [selId]: v.id }));
    setEditDoss(false);
  };

  const openComposeNew = () => {
    setComposeNew(true);
    setPanel(null);
    setTpl("blank");
    setCompose({ to: "", subject: "", text: "" });
    setSendError(null);
  };
  const applyTemplate = (id) => {
    setTpl(id);
    const t = MAIL_TEMPLATES.find((x) => x.id === id);
    if (t) setCompose((c) => ({ ...c, subject: t.subject, text: t.body }));
  };

  const openReply = () => {
    if (!detail?.data) return;
    const { from, subject, date, bodyText } = detail.data;
    setSendError(null);
    setCompose({
      to: from.email,
      subject: /^re\s*:/i.test(subject) ? subject : `RE: ${subject}`,
      text: `\n\n———\nLe ${formatMsgDate(date)}, ${from.name} a écrit :\n${bodyText}`,
    });
    setPanel("reply");
  };

  // "Brouillon IA" (voir MyALFA Worksync Test V2/mails.jsx) : dans la
  // maquette, un brouillon déjà écrit à l'avance, remis en place par ce
  // bouton si l'utilisateur l'a modifié. Ici, un vrai brouillon généré par
  // Claude (endpoint générique déjà existant, controllers/claude), qui
  // remplace entièrement le texte en cours — même geste que la maquette.
  const generateAiReply = async () => {
    if (!detail?.data) return;
    setAiDrafting(true);
    setSendError(null);
    try {
      const { from, subject, bodyText } = detail.data;
      const prompt = `Tu es un collaborateur du cabinet comptable ALFA Partenaires. Rédige une réponse professionnelle et concise en français à ce mail, dans le même esprit que les modèles internes du cabinet (signature "Bien cordialement,\nCabinet ALFA Partenaires"). Réponds uniquement avec le corps du mail à envoyer, sans objet, sans introduction ni commentaire méta.\n\n---\nDe : ${from.name} <${from.email}>\nObjet : ${subject}\n\n${bodyText}\n---`;
      const res = await authFetch("https://backend-myalfa.vercel.app/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok) throw new Error("Erreur serveur");
      const data = await res.json();
      const text = data?.result?.[0]?.text?.trim();
      if (!text) throw new Error("Réponse vide");
      setCompose((c) => ({ ...c, text }));
    } catch (err) {
      setSendError("Erreur lors de la génération du brouillon IA.");
    } finally {
      setAiDrafting(false);
    }
  };

  const openForward = () => {
    if (!detail?.data) return;
    const { from, subject, bodyText } = detail.data;
    setSendError(null);
    setCompose({
      to: "",
      subject: /^tr\s*:/i.test(subject) ? subject : `TR: ${subject}`,
      text: `\n\n———\nDe : ${from.name} <${from.email}>\nObjet : ${subject}\n\n${bodyText}`,
    });
    setPanel("forward");
  };
  // Client réel rattaché au dossier du mail courant (voir pickDoss) — null
  // si aucun dossier rattaché, ou rattaché avant ce champ (nom seul, sans id).
  const dossierClientId = selId ? dossierClientIds[selId] ?? null : null;

  const openTodo = () => {
    if (!detail?.data) return;
    const currentMail = classified.find((m) => m.id === selId);
    setEvenementPrefill({
      clientId: dossierClientId,
      nomClient: dossier || "",
      note: detail.data.subject || "",
      descriptif: detail.data.bodyText || "",
      type: mapMailCategoryToEvenementType(currentMail?.category || "Autre"),
    });
    setPanel(null);
    setShowEvenementOverlay(true);
  };
  const openMission = () => {
    if (!detail?.data) return;
    setMissionPrefill({
      clientId: dossierClientId,
      nomClient: dossier || "",
      commentaire: [detail.data.subject, detail.data.bodyText].filter(Boolean).join("\n\n"),
    });
    setPanel(null);
    setShowMissionOverlay(true);
  };

  const sendNew = async () => {
    setSendError(null);
    try {
      await sendMail({ to: compose.to, subject: compose.subject, body: compose.text });
      setComposeNew(false);
      flash(`Mail envoyé à ${compose.to || "—"}`);
      setCompose({ to: "", subject: "", text: "" });
      setTpl("blank");
    } catch (err) {
      setSendError(err.response?.data?.message || "Erreur lors de l'envoi.");
    }
  };
  const sendCompose = async () => {
    setSendError(null);
    try {
      await sendMail({ to: compose.to, subject: compose.subject, body: compose.text });
      setPanel(null);
      flash(panel === "forward" ? "Mail transféré" : "Réponse envoyée");
    } catch (err) {
      setSendError(err.response?.data?.message || "Erreur lors de l'envoi.");
    }
  };

  const nextAfterArchive = (id) => {
    const remaining = list.filter((m) => m.id !== id);
    setSelId(remaining[0]?.id || null);
  };

  const archiveWithLabel = async () => {
    if (!dossier) {
      const ok = await dialog.confirm(
        "Aucun dossier n'est rattaché.\n\nCe mail sera archivé dans Gmail (retiré de la boîte de réception) et masqué dans MyALFA.\n\nArchiver quand même ?"
      );
      if (!ok) return;
    }
    const id = selId;
    setArchivedIds((prev) => new Set(prev).add(id));
    setPanel(null);
    nextAfterArchive(id);
    try {
      await archiveMessage(id);
      flash(dossier ? `Archivé (rattaché à « ${dossier} ») · aussi archivé dans Gmail` : "Archivé dans MyALFA et dans Gmail");
    } catch (err) {
      flash(err.response?.data?.message || "Archivé dans MyALFA, mais l'archivage Gmail a échoué.");
    }
  };

  const phone = detail?.data ? detectPhone(detail.data.bodyText) : null;

  if (!connected) {
    return (
      <div className="max-w-[1400px] mx-auto">
        {onBack && (
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs mb-3" style={{ color: INK_3 }}>
            <ArrowLeft size={14} /> Retour au Bureau
          </button>
        )}
        <div className="flex items-center gap-2 mb-5">
          <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
            Mails
          </h1>
          <span className="text-xs font-semibold px-2 py-0.5" style={{ background: BURGUNDY_TINT, color: BURGUNDY, borderRadius: 999 }}>
            IA
          </span>
        </div>
        {/* La demande de connexion se fait désormais via la fenêtre globale
            proposée juste après la connexion à l'app (voir
            ConnectGoogleServicesModal) — ce lien reste seulement en filet de
            rattrapage pour qui l'aurait ignorée. */}
        <div className="flex flex-col items-center gap-2 px-8 py-16 text-center">
          {error && (
            <p className="text-xs mb-1" style={{ color: BAD_INK }}>
              {error}
            </p>
          )}
          <button
            onClick={() => connect()}
            disabled={connecting}
            className="text-xs font-semibold disabled:opacity-60"
            style={{ color: BURGUNDY }}
          >
            {connecting ? "Connexion…" : "Se connecter à Gmail"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto flex flex-col" style={{ height: "calc(100vh - 120px)" }}>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs mb-3 flex-shrink-0 self-start" style={{ color: INK_3 }}>
          <ArrowLeft size={14} /> Retour au Bureau
        </button>
      )}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
              Mails
            </h1>
            <span className="text-xs font-semibold px-2 py-0.5" style={{ background: BURGUNDY_TINT, color: BURGUNDY, borderRadius: 999 }}>
              IA
            </span>
            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              title="Voir le tutoriel"
              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
            >
              <HelpCircle size={14} />
            </button>
          </div>
          <p className="text-xs mt-1" style={{ color: INK_3 }}>
            {email} · {unreadTotal} non lus
          </p>
        </div>
        <button
          onClick={openComposeNew}
          className="flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-white"
          style={{ background: BURGUNDY, borderRadius: 10 }}
          onMouseEnter={(e) => (e.currentTarget.style.background = BURGUNDY_SOFT)}
          onMouseLeave={(e) => (e.currentTarget.style.background = BURGUNDY)}
        >
          <Plus size={14} /> Nouveau mail
        </button>
      </div>

      <div className="flex items-center gap-2.5 flex-wrap mb-5 flex-shrink-0">
        <div className="relative" style={{ maxWidth: 280, flex: "1 1 auto" }}>
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3" style={{ color: INK_3 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un mail, un client…"
            className="w-full h-9 pl-8 pr-3 text-xs outline-none"
            style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK_2, background: "#fff" }}
          />
        </div>
        <button onClick={fetchMessages} title="Actualiser" className="p-2" style={{ color: INK_3 }}>
          <RefreshCw size={14} className={loadingMessages ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid gap-4 flex-1 min-h-0" style={{ gridTemplateColumns: "minmax(300px, 360px) 1fr" }}>
        {/* Liste */}
        <div className="flex flex-col min-h-0" style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14 }}>
          <div className="flex-1 overflow-y-auto p-1.5">
            {error && (
              <p className="text-xs px-3 py-2" style={{ color: BAD_INK }}>
                {error}
              </p>
            )}
            {list.map((m) => {
              const unread = m.unread && !readIds.has(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => openMail(m.id)}
                  className="w-full flex flex-col gap-1 text-left px-3 py-2.5"
                  style={{ borderBottom: `1px solid ${LINE}`, borderRadius: 10, background: selId === m.id && !composeNew ? BURGUNDY_TINT : "transparent" }}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {unread && <span className="flex-shrink-0" style={{ width: 6, height: 6, borderRadius: "50%", background: BURGUNDY }} />}
                      <span className="text-[12px] truncate" style={{ color: unread ? BURGUNDY : INK, fontWeight: unread ? 700 : 500 }}>
                        {m.from.name}
                      </span>
                    </span>
                    <span className="text-[10px] flex-shrink-0" style={{ color: INK_3 }}>
                      {formatMsgDate(m.date)}
                    </span>
                  </span>
                  <span className="text-[11.5px] truncate" style={{ color: INK, fontWeight: unread ? 600 : 400 }}>
                    {m.subject}
                  </span>
                  <span className="text-[10.5px] truncate" style={{ color: INK_3 }}>
                    {m.snippet}
                  </span>
                </button>
              );
            })}
            {!loadingMessages && list.length === 0 && (
              <div className="flex flex-col items-center gap-2 text-center py-16 text-xs" style={{ color: INK_3 }}>
                <Mail size={20} />
                Aucun mail
              </div>
            )}
            {loadingMessages && Array.from({ length: 6 }).map((_, i) => <MailRowSkeleton key={`sk-${i}`} />)}
            {!loadingMessages && nextPageToken && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold disabled:opacity-60"
                style={{ color: BURGUNDY }}
              >
                <ChevronDown size={13} /> {loadingMore ? "Chargement…" : "Charger plus de mails"}
              </button>
            )}
          </div>
        </div>

        {/* Lecture */}
        <div className="flex flex-col min-h-0" style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14 }}>
          {composeNew ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
                <div className="flex-shrink-0 flex items-center justify-center text-white" style={{ width: 42, height: 42, borderRadius: "50%", background: BURGUNDY }}>
                  <Send size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[16px] font-semibold" style={{ color: INK }}>
                    Nouveau mail
                  </div>
                  <div className="text-[12.5px]" style={{ color: INK_2 }}>
                    Depuis {email}
                  </div>
                </div>
                <button onClick={() => setComposeNew(false)} title="Fermer" className="flex-shrink-0 flex items-center justify-center" style={{ color: INK_3 }}>
                  <X size={16} />
                </button>
              </div>
              <div className="flex flex-col gap-3 px-5 py-4 overflow-y-auto">
                {sendError && (
                  <p className="text-xs" style={{ color: BAD_INK }}>
                    {sendError}
                  </p>
                )}
                <label className="grid items-center gap-3" style={{ gridTemplateColumns: "70px 1fr" }}>
                  <span className="text-xs font-semibold" style={{ color: INK_3 }}>
                    Modèle
                  </span>
                  <Select value={tpl} onChange={(e) => applyTemplate(e.target.value)}>
                    {MAIL_TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </label>
                <label className="grid items-center gap-3" style={{ gridTemplateColumns: "70px 1fr" }}>
                  <span className="text-xs font-semibold" style={{ color: INK_3 }}>
                    À
                  </span>
                  <input
                    className={inputClass}
                    style={inputStyle}
                    placeholder="destinataire@email.re"
                    value={compose.to}
                    onChange={(e) => setCompose({ ...compose, to: e.target.value })}
                  />
                </label>
                <label className="grid items-center gap-3" style={{ gridTemplateColumns: "70px 1fr" }}>
                  <span className="text-xs font-semibold" style={{ color: INK_3 }}>
                    Objet
                  </span>
                  <input
                    className={inputClass}
                    style={inputStyle}
                    placeholder="Objet du mail"
                    value={compose.subject}
                    onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                  />
                </label>
                <textarea
                  className="w-full px-3 py-2.5 text-sm outline-none resize-y"
                  style={{ border: `1px solid ${LINE}`, borderRadius: 8, color: INK, minHeight: 220 }}
                  placeholder="Rédigez votre message…"
                  value={compose.text}
                  onChange={(e) => setCompose({ ...compose, text: e.target.value })}
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setComposeNew(false)} className="px-3.5 py-2 text-xs" style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK }}>
                    Annuler
                  </button>
                  <button
                    onClick={sendNew}
                    disabled={!compose.to || !compose.subject}
                    className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: BURGUNDY, borderRadius: 10 }}
                  >
                    <Send size={12} /> Envoyer
                  </button>
                </div>
              </div>
            </div>
          ) : !selId ? (
            <div className="h-full flex items-center justify-center text-center px-6 text-xs" style={{ color: INK_3 }}>
              Sélectionnez un message pour l'afficher
            </div>
          ) : detail?.loading && !detail?.data ? (
            <MailDetailSkeleton />
          ) : detail?.error && !detail?.data ? (
            <div className="h-full flex items-center justify-center text-xs px-6 text-center" style={{ color: BAD_INK }}>
              {detail.error}
            </div>
          ) : detail?.data ? (
            <div className="flex flex-col h-full min-h-0">
              <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
              <div className="flex items-start gap-3 px-4 py-3.5 sticky top-0 bg-white z-10" style={{ borderBottom: `1px solid ${LINE}` }}>
                <div className="flex-shrink-0 flex items-center justify-center text-white text-[12px] font-bold" style={{ width: 36, height: 36, borderRadius: "50%", background: BURGUNDY }}>
                  {initialsOf(detail.data.from.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold truncate" style={{ color: INK }}>
                    {detail.data.subject}
                  </div>
                  <div className="text-[11.5px] truncate mt-0.5" style={{ color: INK_2 }}>
                    {detail.data.from.name} <span style={{ color: INK_3 }}>&lt;{detail.data.from.email}&gt;</span>
                  </div>
                  <div className="mt-2">
                    {dossier && !editDoss ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold"
                        style={{ background: BURGUNDY_TINT, color: BURGUNDY, borderRadius: 999, padding: "4px 6px 4px 11px" }}
                      >
                        <Folder size={11} /> {dossier}
                        <button onClick={clearDoss} className="flex items-center justify-center" style={{ width: 16, height: 16, borderRadius: "50%" }}>
                          <X size={9} />
                        </button>
                      </span>
                    ) : (
                      <div style={{ maxWidth: 240 }}>
                        <DossierPicker value={dossier ? { id: dossierClientId, nom: dossier } : null} onChange={pickDoss} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <span className="text-[11px]" style={{ color: INK_3 }}>
                    {formatMsgDate(detail.data.date)}
                  </span>
                  <a href={gmailMessageUrl(detail.data.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px]" style={{ color: INK_3 }}>
                    <ExternalLink size={10} /> Gmail
                  </a>
                </div>
              </div>

              <div className="flex-1 flex flex-col px-4 pt-3.5 pb-16">
                <div className="flex-1 flex flex-col">
                  {detail.data.bodyHtml ? (
                    <HtmlEmailBody html={detail.data.bodyHtml} />
                  ) : (
                    detail.data.bodyText.split("\n").map((line, i) => (
                      <p key={i} className="text-[12.5px] leading-relaxed" style={{ color: INK, margin: line ? "0 0 11px" : 0 }}>
                        {line}
                      </p>
                    ))
                  )}
                </div>

                {detail.data.attachments.length > 0 && (
                  <div className="flex flex-col gap-1.5 mt-1">
                    <div className="flex items-center gap-1.5 text-[10px]" style={{ color: INK_3 }}>
                      <Info size={11} /> Pièces jointes non copiées dans MyALFA — consultation dans Gmail
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {detail.data.attachments.map((a, i) => (
                        <a
                          key={i}
                          href={gmailMessageUrl(detail.data.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5"
                          style={{ background: "#FAF7F4", border: `1px solid ${LINE}`, borderRadius: 8, color: INK_2 }}
                        >
                          <FileText size={12} /> {a.filename} <ExternalLink size={10} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {phone && !st.phoneAdded && (
                  <div className="flex items-center gap-2.5 mt-1 px-3 py-2.5" style={{ background: INFO_BG, color: INFO_INK, borderRadius: 10 }}>
                    <IdCard size={13} className="flex-shrink-0" />
                    <span className="flex-1 text-[11.5px]">
                      Coordonnée détectée <b>{phone}</b>
                    </span>
                    <button
                      onClick={() => {
                        setSt({ phoneAdded: true });
                        flash(dossier ? `Coordonnée ajoutée à la fiche « ${dossier} »` : "Contact créé dans l'annuaire");
                      }}
                      className="flex-shrink-0 text-[10.5px] font-semibold text-white px-2.5 py-1.5"
                      style={{ background: INFO_INK, borderRadius: 7 }}
                    >
                      {dossier ? "Ajouter à la fiche" : "Créer / compléter une fiche"}
                    </button>
                    <button onClick={() => setSt({ phoneAdded: true })} className="flex-shrink-0 flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: "50%" }}>
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>

              {(panel === "reply" || panel === "forward") && (
                <div className="mx-4 mb-4 px-4 py-3.5" style={{ border: `1px solid ${LINE}`, borderRadius: 12, boxShadow: "0 8px 24px rgba(20,14,24,.08)" }}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 text-xs font-bold" style={{ color: BURGUNDY }}>
                      {panel === "forward" ? <Send size={13} /> : <Mail size={13} />} {panel === "forward" ? "Transférer le mail" : "Répondre"}
                    </div>
                    {panel === "reply" && (
                      <button
                        onClick={generateAiReply}
                        disabled={aiDrafting}
                        className="flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-50"
                        style={{ color: INK_3 }}
                      >
                        <Sparkles size={12} className={aiDrafting ? "animate-pulse" : ""} /> {aiDrafting ? "Génération…" : "Brouillon IA"}
                      </button>
                    )}
                  </div>
                  {sendError && (
                    <p className="text-xs mb-2" style={{ color: BAD_INK }}>
                      {sendError}
                    </p>
                  )}
                  <label className={labelClass} style={{ color: INK_3, marginBottom: 10 }}>
                    À
                    <input
                      className={inputClass}
                      style={inputStyle}
                      placeholder="destinataire@email.re"
                      value={compose.to}
                      onChange={(e) => setCompose({ ...compose, to: e.target.value })}
                    />
                  </label>
                  <textarea
                    className="w-full px-3 py-2.5 text-xs outline-none resize-y"
                    style={{ border: `1px solid ${LINE}`, borderRadius: 8, color: INK }}
                    rows={8}
                    value={compose.text}
                    onChange={(e) => setCompose({ ...compose, text: e.target.value })}
                  />
                  <div className="flex justify-end gap-2 mt-3.5">
                    <button onClick={() => setPanel(null)} className="px-3.5 py-2 text-xs" style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK }}>
                      Annuler
                    </button>
                    <button
                      onClick={sendCompose}
                      disabled={!compose.to}
                      className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      style={{ background: BURGUNDY, borderRadius: 10 }}
                    >
                      <Send size={12} /> {panel === "forward" ? "Transférer" : "Envoyer"}
                    </button>
                  </div>
                </div>
              )}
              </div>

              <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 flex-wrap bg-white" style={{ borderTop: `1px solid ${LINE}` }}>
                <button onClick={openReply} className="flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5" style={{ border: `1px solid ${LINE_2}`, borderRadius: 8, color: INK }}>
                  <Mail size={13} /> Répondre
                </button>
                <button onClick={openForward} className="flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5" style={{ border: `1px solid ${LINE_2}`, borderRadius: 8, color: INK }}>
                  <Send size={13} /> Transférer
                </button>
                <span style={{ width: 1, height: 18, background: LINE_2 }} />
                <button
                  onClick={openTodo}
                  className="flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5"
                  style={{ border: `1px solid ${LINE_2}`, borderRadius: 8, color: INK }}
                >
                  <CheckSquare size={13} /> En tâche
                </button>
                <button
                  onClick={openMission}
                  className="flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5"
                  style={{ border: `1px solid ${LINE_2}`, borderRadius: 8, color: INK }}
                >
                  <Handshake size={13} /> En mission ponctuelle
                </button>
                <span className="flex-1" />
                <button onClick={archiveWithLabel} className="flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 text-white" style={{ background: BURGUNDY, borderRadius: 8 }}>
                  <Folder size={13} /> Classer &amp; archiver
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showTutorial && (
        <TutorialOverlay steps={mailsTutorial} onClose={() => setShowTutorial(false)} />
      )}

      {showEvenementOverlay && (
        <div className="overlayAC">
          <div className="inoverlayMS" style={{ maxWidth: 720, height: "90vh" }}>
            <Evenement onCancel={() => setShowEvenementOverlay(false)} prefill={evenementPrefill} />
          </div>
        </div>
      )}

      {showMissionOverlay && (
        <div className="overlayAC">
          <div className="inoverlayMS" style={{ position: "relative" }}>
            <button
              onClick={() => setShowMissionOverlay(false)}
              aria-label="Fermer"
              className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              style={{ position: "absolute", top: 12, right: 12, border: "none", cursor: "pointer", zIndex: 10 }}
            >
              <X className="w-4 h-4" />
            </button>
            <Mission onCancel={() => setShowMissionOverlay(false)} prefill={missionPrefill} />
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs px-4 py-2.5 text-white z-[220]"
          style={{ background: INK, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.24)" }}
        >
          <Check size={13} style={{ color: "#7BE0A0" }} /> {toast}
        </div>
      )}
    </div>
  );
}

export default Mails;
